import React, { useEffect, useState } from "react";

type DotProgressProps = {
  /** Maximum dots to display (e.g. 4 will show: "." ".." "..." "....") */
  maxDots?: number;
  /** Milliseconds between steps */
  interval?: number;
  /** Optional CSS class for the wrapper */
  className?: string;
  /** Accessible label for the animation */
  ariaLabel?: string;
  /** Whether to pause animation */
  paused?: boolean;
};

/**
 * DotProgress
 * A tiny, accessible React + TypeScript component that cycles through
 * "." ".." "..." "...." and repeats.
 *
 * - Default: maxDots=4, interval=400ms
 * - Uses a timer (useEffect + setInterval) so it's simple and reliable.
 *
 * Usage:
 * ```tsx
 * import DotProgress from "./DotProgress.react";
 * <DotProgress interval={300} maxDots={3} />
 * ```
 */
export default function DotProgress({
  maxDots = 5,
  interval = 100,
  className = "",
  ariaLabel = "",
  paused = false,
}: DotProgressProps) {
  const [count, setCount] = useState(1);

  useEffect(() => {
    if (paused) return; // do nothing when paused

    const id = window.setInterval(() => {
      setCount((c) => (c >= maxDots ? 1 : c + 1));
    }, interval);

    return () => window.clearInterval(id);
  }, [interval, maxDots, paused]);

  // Build the dot string safely
  const dots = Array.from({ length: count }).map(() => ".").join("");

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={ariaLabel}
      className={`inline-flex items-center gap-2 text-sm ${className}`}
    >
      <span aria-hidden>{dots}</span>
    </div>
  );
}
